import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faFileCsv, faCheckCircle, faExclamationTriangle,
  faSyncAlt, faEdit, faTrash, faInfoCircle, faCalendarAlt,
  faDollarSign, faTag, faUser, faDatabase
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import { DepensesContext } from '../layout/CunstomDepenses';

// ============================================================
// CACHE DEXIE (avec fallback périmé)
// ============================================================
const db = new Dexie('HistoriqueDepenseCacheDB');
db.version(1).stores({ history: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedHistory(key, allowExpired = false) {
  try {
    const entry = await db.history.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCachedHistory(key, data) {
  await db.history.put({ key, data, timestamp: Date.now() });
}
async function getStaleHistory(key) {
  return getCachedHistory(key, true);
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES EN COURS
// ============================================================
const pendingFetches = new Map();

const API_URL = `${API_CONFIG2.apiUrl}depense/api_historique_depense.php`;

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const HistoriqueDepense = ({ userId }) => {
  const context = useContext(DepensesContext) || {};
  const { refreshKey, setSource } = context;

  // États
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10
  });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [dataSource, setDataSource] = useState('cache');

  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // ============================================================
  // GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success', duration = 5000) => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      if (isMounted.current) setToast(prev => ({ ...prev, show: false }));
    }, duration);
  }, []);

  // ============================================================
  // RÉCUPÉRATION DE L'HISTORIQUE (avec dédoublonnage + annulation)
  // ============================================================
  const fetchHistory = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `page_${page}_search_${search}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage : si une requête identique est en cours, on l'attend
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setHistory(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          if (setSource) setSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives pour éviter les boucles infinies
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    const fetchPromise = (async () => {
      try {
        // 1. Cache (sauf forceRefresh)
        if (!forceRefresh) {
          const cached = await getCachedHistory(cacheKey);
          if (cached && isMounted.current) {
            setHistory(cached.data);
            setPagination(cached.pagination);
            setDataSource('cache');
            if (setSource) setSource('cache');
            setLoading(false);
            setRefreshing(false);
            return cached;
          }
        }

        // 2. Requête réseau
        const url = `${API_URL}?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}`;
        const response = await fetch(url, { signal: controller.signal });

        // Gestion 429
        if (response.status === 429) {
          const stale = await getStaleHistory(cacheKey);
          if (stale && isMounted.current) {
            setHistory(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast('429 – Utilisation du cache périmé', 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchHistory(page, search, forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleHistory(cacheKey);
          if (stale && isMounted.current) {
            setHistory(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast(`Erreur réseau – Utilisation du cache`, 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          const result = { data: data.data, pagination: data.pagination };
          if (isMounted.current) {
            setHistory(data.data);
            setPagination(data.pagination);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            if (setSource) setSource(source);
            await setCachedHistory(cacheKey, result);
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
          }
          return result;
        } else {
          throw new Error(data.message || 'Erreur inconnue');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await getStaleHistory(cacheKey);
        if (stale && isMounted.current) {
          setHistory(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          showToast(`Erreur – Utilisation du cache : ${err.message}`, 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(err.message || 'Erreur de chargement', 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit, showToast, setSource]);

  // ============================================================
  // RÉACTION AU REFRESH GLOBAL (via contexte)
  // ============================================================
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchHistory(pagination.currentPage, searchTerm, true);
    }
  }, [refreshKey, fetchHistory, pagination.currentPage, searchTerm]);

  // ============================================================
  // RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchHistory(1, val, false);
    }, 400);
  };

  // ============================================================
  // CHARGEMENT INITIAL ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchHistory();
    return () => {
      isMounted.current = false;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      pendingFetches.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // EXPORT CSV
  // ============================================================
  const exportCSV = () => {
    if (!history.length) {
      showToast('Aucune donnée à exporter.', 'warning');
      return;
    }
    const headers = ['ID', 'Dépense ID', 'Action', 'Ancien montant', 'Nouveau montant', 'Description', 'Catégorie', 'Utilisateur', 'Date'];
    const rows = history.map(h => [
      h.id,
      h.depense_id,
      h.action,
      h.ancien_montant ?? '',
      h.nouveau_montant ?? '',
      h.depense_description || h.description || '',
      h.depense_categorie || h.categorie || '',
      h.user_id || '',
      h.date_action
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `historique_depense_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ============================================================
  // MODALE DE DÉTAILS
  // ============================================================
  const openDetailModal = (record) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const getActionBadge = (action) => {
    const lower = action?.toLowerCase() || '';
    if (lower === 'insert' || lower === 'create' || lower === 'import') return { label: 'Création', color: '#3b82f6' };
    if (lower === 'update' || lower === 'edit') return { label: 'Modification', color: '#f59e0b' };
    if (lower === 'delete') return { label: 'Suppression', color: '#ef4444' };
    return { label: action || 'Inconnu', color: '#94a3b8' };
  };

  // ============================================================
  // AFFICHAGE DU CHARGEMENT
  // ============================================================
  if (loading && history.length === 0) {
    return (
      <div className="historique-page">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="text-secondary mt-2">Chargement de l'historique...</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDU AVEC STYLES RESPONSIFS OPTIMISÉS
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – HISTORIQUE DEPENSE (mobile optimisé)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .historique-page {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: 24px;
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.p-2 {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 16px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
          max-width: 100%;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }
        .glass-table th {
          padding: 12px 10px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
          font-weight: 600;
        }
        .glass-table td {
          padding: 10px 10px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .glass-table tr {
          transition: 0.2s;
        }
        .glass-table tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .action-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: clamp(0.6rem, 0.8vw, 0.7rem);
          font-weight: 600;
        }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
          padding: clamp(1rem, 1.5vw, 1.5rem) clamp(1.2rem, 2vw, 2rem);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
          padding: clamp(0.8rem, 1.5vw, 1.2rem) clamp(1.2rem, 2vw, 2rem);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }
        .monetra-modal .modal-body {
          padding: clamp(1.2rem, 2vw, 2rem);
        }

        .toast-monetra {
          background: rgba(10, 20, 45, 0.8) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }

        .text-secondary {
          color: var(--text-secondary);
        }
        .text-muted {
          color: var(--text-muted);
        }
        .text-success {
          color: var(--accent-blue);
        }
        .text-danger {
          color: #ef4444;
        }
        .fw-bold {
          font-weight: 600;
        }
        .small {
          font-size: clamp(0.65rem, 0.8vw, 0.85rem);
        }
        .m-0 {
          margin: 0;
        }
        .mb-4 {
          margin-bottom: clamp(1rem, 2vw, 1.5rem);
        }
        .me-2 {
          margin-right: 0.5rem;
        }
        .p-2 {
          padding: 0.5rem;
        }
        .p-3 {
          padding: 1rem;
        }
        .py-5 {
          padding-top: 3rem;
          padding-bottom: 3rem;
        }
        .gap-2 {
          gap: 0.5rem;
        }
        .gap-3 {
          gap: 1rem;
        }
        .d-flex {
          display: flex;
        }
        .flex-wrap {
          flex-wrap: wrap;
        }
        .align-items-center {
          align-items: center;
        }
        .justify-content-between {
          justify-content: space-between;
        }
        .text-end {
          text-align: right;
        }
        .text-center {
          text-align: center;
        }
        .overflow-hidden {
          overflow: hidden;
        }
        .table-responsive {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        .border-top {
          border-top: 1px solid rgba(255,255,255,0.04);
        }
        .border-opacity-10 {
          opacity: 0.1;
        }

        /* ============================================================
           RESPONSIVE MOBILE
           ============================================================ */
        @media (max-width: 768px) {
          .historique-page { padding: 12px; }
          .glass-card { padding: 1rem; }

          .d-flex.justify-content-between.align-items-center {
            flex-direction: column;
            align-items: stretch !important;
            gap: 12px;
          }
          .d-flex.justify-content-between.align-items-center > div:first-child {
            text-align: center;
          }
          .d-flex.gap-2 {
            flex-wrap: wrap;
            justify-content: center;
          }

          .search-wrapper {
            max-width: 100%;
            min-width: unset;
          }

          .glass-table th,
          .glass-table td {
            padding: 6px 8px;
            font-size: 0.7rem;
          }
          .glass-table th:last-child,
          .glass-table td:last-child {
            min-width: 80px;
          }

          .monetra-btn {
            font-size: 0.75rem;
            padding: 6px 12px;
            min-height: 38px;
            flex: 1 0 auto;
            justify-content: center;
          }
          .monetra-btn.p-2 {
            padding: 4px 8px;
            min-width: 32px;
          }

          .monetra-modal .modal-footer {
            flex-direction: column-reverse;
          }
          .monetra-modal .modal-footer button {
            width: 100%;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .monetra-btn {
            font-size: 0.7rem;
            padding: 4px 10px;
            min-height: 34px;
          }
          .glass-table th,
          .glass-table td {
            font-size: 0.6rem;
            padding: 4px 6px;
          }
        }
      `}</style>

      <div className="historique-page">
        {/* En-tête avec badge source et boutons d'actualisation */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="fw-bold mb-1" style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)' }}>
              Historique des dépenses
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Consultez toutes les modifications apportées aux dépenses.
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn"
              onClick={() => fetchHistory(pagination.currentPage, searchTerm, false)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Actualiser
            </button>
            <button
              className="monetra-btn"
              onClick={() => fetchHistory(pagination.currentPage, searchTerm, true)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
            <button className="monetra-btn" onClick={exportCSV} disabled={history.length === 0 || loading || refreshing}>
              <FontAwesomeIcon icon={faFileCsv} /> Exporter CSV
            </button>
          </div>
        </div>

        {/* Recherche */}
        <div className="glass-card d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="search-wrapper" style={{ minWidth: '260px', flex: 1, maxWidth: '100%' }}>
            <FontAwesomeIcon icon={faSearch} className="text-secondary" />
            <input
              type="text"
              placeholder="Rechercher (action, description, catégorie...)"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="text-secondary small">
            <span>{pagination.totalRecords} enregistrements</span>
          </div>
        </div>

        {/* Tableau */}
        <div className="glass-card p-0 overflow-hidden mt-4">
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Dépense</th>
                  <th>Action</th>
                  <th>Ancien montant</th>
                  <th>Nouveau montant</th>
                  <th>Description</th>
                  <th>Catégorie</th>
                  <th>Utilisateur</th>
                  <th>Date</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && !refreshing ? (
                  <tr><td colSpan="10" className="text-center py-5"><div className="spinner-border text-primary" role="status" /></td></tr>
                ) : history.length === 0 ? (
                  <tr><td colSpan="10" className="text-center py-5 text-secondary">Aucun historique trouvé.</td></tr>
                ) : (
                  history.map(item => {
                    const { label, color } = getActionBadge(item.action);
                    return (
                      <tr key={item.id}>
                        <td><span className="fw-bold text-secondary">#{item.id}</span></td>
                        <td><span className="text-secondary">#{item.depense_id}</span></td>
                        <td>
                          <span className="action-badge" style={{ backgroundColor: color + '22', color: color }}>
                            {label}
                          </span>
                        </td>
                        <td><span className="text-secondary">{item.ancien_montant !== null ? item.ancien_montant.toFixed(2) + '$' : '-'}</span></td>
                        <td><span className="text-secondary">{item.nouveau_montant !== null ? item.nouveau_montant.toFixed(2) + '$' : '-'}</span></td>
                        <td><span className="text-secondary">{item.depense_description || item.description || '-'}</span></td>
                        <td><span className="text-secondary">{item.depense_categorie || item.categorie || '-'}</span></td>
                        <td><span className="text-secondary">{item.user_id || 'Système'}</span></td>
                        <td><span className="text-secondary" style={{ fontSize: '0.85rem' }}>{new Date(item.date_action).toLocaleString()}</span></td>
                        <td className="text-end">
                          <button className="monetra-btn p-2" onClick={() => openDetailModal(item)} disabled={loading || refreshing}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-3 border-top border-secondary border-opacity-10 d-flex justify-content-between align-items-center flex-wrap">
            <span className="text-secondary small">Page {pagination.currentPage} sur {pagination.totalPages}</span>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchHistory(pagination.currentPage - 1, searchTerm, false)}
              >
                ‹
              </button>
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchHistory(pagination.currentPage + 1, searchTerm, false)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modale de détails */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered className="monetra-modal" size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold" style={{ fontSize: 'clamp(1.1rem, 2vw, 1.3rem)' }}>
            <FontAwesomeIcon icon={faInfoCircle} className="text-success me-2" />
            Détails de l'enregistrement #{selectedRecord?.id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <div className="row g-3">
              <div className="col-md-6">
                <div className="text-secondary small">ID</div>
                <div>#{selectedRecord.id}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Dépense ID</div>
                <div>#{selectedRecord.depense_id}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Action</div>
                <div>
                  <span className="action-badge" style={{ backgroundColor: getActionBadge(selectedRecord.action).color + '22', color: getActionBadge(selectedRecord.action).color }}>
                    {getActionBadge(selectedRecord.action).label}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Utilisateur</div>
                <div>{selectedRecord.user_id || 'Système'}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Ancien montant</div>
                <div>{selectedRecord.ancien_montant !== null ? selectedRecord.ancien_montant.toFixed(2) + '$' : '-'}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Nouveau montant</div>
                <div>{selectedRecord.nouveau_montant !== null ? selectedRecord.nouveau_montant.toFixed(2) + '$' : '-'}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Description</div>
                <div>{selectedRecord.depense_description || selectedRecord.description || '-'}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Catégorie</div>
                <div>{selectedRecord.depense_categorie || selectedRecord.categorie || '-'}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Date</div>
                <div>{new Date(selectedRecord.date_action).toLocaleString()}</div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDetailModal(false)} className="monetra-btn outline-secondary">Fermer</Button>
        </Modal.Footer>
      </Modal>

      {/* Toast de notification */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <div className={`toast-monetra p-3 ${toast.variant}`} style={{ minWidth: 'clamp(200px, 30vw, 300px)' }}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
              />
              <span style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)' }}>{toast.message}</span>
              <button
                className="btn-close-custom ms-auto"
                onClick={() => setToast({ ...toast, show: false })}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', opacity: 0.5, fontSize: '1.2rem' }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HistoriqueDepense;